import { FormEvent, useEffect, useMemo, useState } from 'react'
import { WORK_UNITS } from '../../lib/dailyContext'
import { printAnnouncementReport } from '../../lib/announcementReport'
import {
  closeAnnouncement,
  createAnnouncement,
  deleteArchivedAnnouncementNow,
  loadAnnouncementFollowup,
  loadAnnouncementManagement,
  markAnnouncementReportGenerated,
  type AnnouncementFollowupRecipient,
  type AnnouncementFollowupStatus,
  type AnnouncementManagementRow,
  type AnnouncementPriority,
  type AnnouncementScope,
} from '../../lib/announcements'

type FormState = {
  title: string
  body: string
  referenceCode: string
  priority: AnnouncementPriority
  targetScope: AnnouncementScope
  targetUnitKey: string
  requireAck: boolean
  pinned: boolean
  expiresAt: string
}

const EMPTY: FormState = {
  title: '',
  body: '',
  referenceCode: '',
  priority: 'alerta',
  targetScope: 'all',
  targetUnitKey: '',
  requireAck: true,
  pinned: false,
  expiresAt: '',
}

const FOLLOWUP_LABELS: Record<AnnouncementFollowupStatus, string> = {
  nao_visualizado: 'Não visualizou',
  visualizado: 'Visualizou, sem ciência',
  ciente: 'Ciente',
}

function targetLabel(item: AnnouncementManagementRow) {
  if (item.target_scope === 'all') return 'Todos'
  if (item.target_scope === 'presencial') return 'Atendimento presencial'
  if (item.target_scope === 'call-center') return 'Call Center'
  return WORK_UNITS.find((unit) => unit.key === item.target_unit_key)?.label ?? 'Unidade específica'
}

function formatDate(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('pt-BR')
}

function retentionText(deleteAfter: string | null, now: number) {
  if (!deleteAfter) return ''
  const remaining = new Date(deleteAfter).getTime() - now
  if (remaining <= 0) return 'Exclusão pendente'
  const totalMinutes = Math.ceil(remaining / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours <= 0) return `Exclusão automática em ${minutes} min`
  return `Exclusão automática em ${hours}h ${minutes.toString().padStart(2, '0')}min`
}

export function AnnouncementsAdmin() {
  const [rows, setRows] = useState<AnnouncementManagementRow[]>([])
  const [form, setForm] = useState<FormState>(EMPTY)
  const [creating, setCreating] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [followupId, setFollowupId] = useState<number | null>(null)
  const [followupRows, setFollowupRows] = useState<AnnouncementFollowupRecipient[]>([])
  const [followupLoading, setFollowupLoading] = useState(false)
  const [followupError, setFollowupError] = useState('')
  const [followupSearch, setFollowupSearch] = useState('')
  const [followupStatus, setFollowupStatus] = useState<'todos' | AnnouncementFollowupStatus>('todos')
  const [followupUnit, setFollowupUnit] = useState('todos')
  const [now, setNow] = useState(() => Date.now())

  async function load() {
    setLoading(true)
    try {
      setRows(await loadAnnouncementManagement())
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar os comunicados.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const activeCount = useMemo(() => rows.filter((row) => row.status === 'ativo').length, [rows])
  const criticalCount = useMemo(() => rows.filter((row) => row.status === 'ativo' && row.priority === 'critico').length, [rows])
  const awaitingArchiveCount = useMemo(() => rows.filter((row) => row.status === 'encerrado').length, [rows])

  const followupCounts = useMemo(() => ({
    nao_visualizado: followupRows.filter((item) => item.status === 'nao_visualizado').length,
    visualizado: followupRows.filter((item) => item.status === 'visualizado').length,
    ciente: followupRows.filter((item) => item.status === 'ciente').length,
  }), [followupRows])

  const followupUnits = useMemo(() => Array.from(
    new Map(followupRows.map((item) => [item.unit_key, item.unit_label])).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1], 'pt-BR')), [followupRows])

  const filteredFollowup = useMemo(() => {
    const term = followupSearch.trim().toLocaleLowerCase('pt-BR')
    return followupRows.filter((item) => {
      if (followupStatus !== 'todos' && item.status !== followupStatus) return false
      if (followupUnit !== 'todos' && item.unit_key !== followupUnit) return false
      if (!term) return true
      return [item.nome, item.email, item.unit_label].some((value) => value.toLocaleLowerCase('pt-BR').includes(term))
    })
  }, [followupRows, followupSearch, followupStatus, followupUnit])

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await createAnnouncement({
        title: form.title,
        body: form.body,
        referenceCode: form.referenceCode,
        priority: form.priority,
        targetScope: form.targetScope,
        targetUnitKey: form.targetScope === 'unit' ? form.targetUnitKey : null,
        requireAck: form.requireAck,
        pinned: form.pinned,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
      })
      setForm(EMPTY)
      setCreating(false)
      setMessage('Comunicado publicado com sucesso.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível publicar o comunicado.')
    } finally {
      setSaving(false)
    }
  }

  async function close(id: number) {
    if (!window.confirm('Encerrar este comunicado? Ele deixará de aparecer como ativo para os colaboradores.')) return
    setSaving(true)
    setMessage('')
    try {
      await closeAnnouncement(id)
      setMessage('Comunicado encerrado. Gere o relatório PDF quando quiser iniciar a retenção de 24 horas.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível encerrar o comunicado.')
    } finally {
      setSaving(false)
    }
  }

  async function toggleFollowup(id: number) {
    if (followupId === id) {
      setFollowupId(null)
      return
    }

    setFollowupId(id)
    setFollowupRows([])
    setFollowupSearch('')
    setFollowupStatus('todos')
    setFollowupUnit('todos')
    setFollowupLoading(true)
    setFollowupError('')

    try {
      setFollowupRows(await loadAnnouncementFollowup(id))
    } catch (err) {
      setFollowupError(err instanceof Error ? err.message : 'Não foi possível carregar o acompanhamento.')
    } finally {
      setFollowupLoading(false)
    }
  }

  async function exportReport(row: AnnouncementManagementRow) {
    try {
      printAnnouncementReport(row, followupRows, targetLabel(row))
      setFollowupError('')

      if (row.status === 'encerrado') {
        const retention = await markAnnouncementReportGenerated(row.id)
        if (retention?.retention_started) {
          setMessage('Relatório gerado. Este comunicado será excluído automaticamente 24 horas após a primeira geração do relatório.')
          await load()
        }
      }
    } catch (err) {
      setFollowupError(err instanceof Error ? err.message : 'Não foi possível gerar o relatório.')
    }
  }

  async function deleteNow(row: AnnouncementManagementRow) {
    if (!window.confirm('Excluir definitivamente este comunicado agora? O resumo estatístico anônimo será preservado, mas mensagem, destinatários e registros nominais serão removidos.')) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await deleteArchivedAnnouncementNow(row.id)
      if (followupId === row.id) setFollowupId(null)
      setMessage('Comunicado arquivado e excluído definitivamente. Apenas indicadores estatísticos anônimos foram preservados.')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível excluir o comunicado.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="announcements-admin">
      <div className="admin-section-title">
        <div>
          <p className="eyebrow">COMUNICAÇÃO OPERACIONAL</p>
          <h2>Centro de Comunicados</h2>
          <p className="muted">Publique orientações oficiais, segmente o público e acompanhe leitura e ciência.</p>
        </div>
        <button className="primary-button compact" type="button" onClick={() => setCreating((value) => !value)}>{creating ? 'Fechar' : '+ Novo comunicado'}</button>
      </div>

      <div className="editor-summary announcements-summary">
        <article><span>Ativos</span><strong>{activeCount}</strong><small>comunicados vigentes</small></article>
        <article><span>Críticos</span><strong>{criticalCount}</strong><small>prioridade máxima</small></article>
        <article><span>Aguardando arquivamento</span><strong>{awaitingArchiveCount}</strong><small>encerrados ainda disponíveis</small></article>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      {creating && (
        <section className="editor-form-card announcement-form-card">
          <form className="editor-form" onSubmit={submit}>
            <label>Título<input value={form.title} onChange={(e) => setForm((v) => ({ ...v, title: e.target.value }))} required /></label>
            <label>Referência / incidente<input value={form.referenceCode} onChange={(e) => setForm((v) => ({ ...v, referenceCode: e.target.value }))} placeholder="Ex.: INC0657904" /></label>
            <label>Prioridade
              <select value={form.priority} onChange={(e) => setForm((v) => ({ ...v, priority: e.target.value as AnnouncementPriority }))}>
                <option value="info">Informação</option><option value="alerta">Alerta</option><option value="critico">Crítico</option>
              </select>
            </label>
            <label>Público
              <select value={form.targetScope} onChange={(e) => setForm((v) => ({ ...v, targetScope: e.target.value as AnnouncementScope }))}>
                <option value="all">Todos</option><option value="presencial">Atendimento presencial</option><option value="call-center">Call Center</option><option value="unit">Unidade específica</option>
              </select>
            </label>
            {form.targetScope === 'unit' && <label>Unidade
              <select value={form.targetUnitKey} onChange={(e) => setForm((v) => ({ ...v, targetUnitKey: e.target.value }))} required>
                <option value="">Selecione...</option>{WORK_UNITS.map((unit) => <option key={unit.key} value={unit.key}>{unit.label}</option>)}
              </select>
            </label>}
            <label>Expira em<input type="datetime-local" value={form.expiresAt} onChange={(e) => setForm((v) => ({ ...v, expiresAt: e.target.value }))} /></label>
            <label className="span-2">Mensagem<textarea rows={9} value={form.body} onChange={(e) => setForm((v) => ({ ...v, body: e.target.value }))} required /></label>
            <label className="checkbox-field"><input type="checkbox" checked={form.requireAck} onChange={(e) => setForm((v) => ({ ...v, requireAck: e.target.checked }))} /><span>Exigir confirmação “Estou ciente”</span></label>
            <label className="checkbox-field"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm((v) => ({ ...v, pinned: e.target.checked }))} /><span>Fixar entre os primeiros comunicados</span></label>
            <div className="form-actions span-2"><button className="primary-button compact" disabled={saving}>{saving ? 'Publicando...' : 'Publicar comunicado'}</button></div>
          </form>
        </section>
      )}

      {loading ? <div className="empty-state">Carregando comunicados...</div> : (
        <div className="announcement-admin-list">
          {rows.length === 0 && <div className="empty-state">Nenhum comunicado ativo ou aguardando arquivamento.</div>}
          {rows.map((row) => (
            <article key={row.id} className={`announcement-admin-card ${row.priority}`}>
              <div className="announcement-admin-head">
                <div>
                  <span className="status-chip">{row.status}</span>
                  <strong>{row.reference_code ? `${row.reference_code} · ` : ''}{row.title}</strong>
                  <small>{targetLabel(row)} · publicado {formatDate(row.created_at)}</small>
                  {row.status === 'encerrado' && !row.report_generated_at && <small className="announcement-archive-state waiting">Aguardando geração do relatório para iniciar a retenção de 24h.</small>}
                  {row.status === 'encerrado' && row.report_generated_at && <small className="announcement-archive-state scheduled">📄 Relatório gerado · {retentionText(row.delete_after, now)}</small>}
                </div>
                <div className="announcement-admin-actions">
                  {row.recipients > 0 && <button className="secondary-button compact" type="button" onClick={() => void toggleFollowup(row.id)}>{followupId === row.id ? 'Fechar acompanhamento' : 'Ver acompanhamento'}</button>}
                  {row.status === 'ativo' && <button className="secondary-button compact" type="button" disabled={saving} onClick={() => void close(row.id)}>Encerrar</button>}
                  {row.status === 'encerrado' && row.report_generated_at && <button className="danger-button compact" type="button" disabled={saving} onClick={() => void deleteNow(row)}>Excluir agora</button>}
                </div>
              </div>

              <div className="announcement-admin-metrics">
                <span><b>{row.recipients}</b> destinatários</span>
                <span><b>{row.viewed}</b> visualizações</span>
                <span><b>{row.acknowledged}</b> ciências</span>
                <span><b>{Number(row.viewed_pct).toLocaleString('pt-BR')}%</b> leitura</span>
                <span><b>{Number(row.acknowledged_pct).toLocaleString('pt-BR')}%</b> ciência</span>
                <span><b>{row.avg_ack_minutes == null ? '—' : `${Number(row.avg_ack_minutes).toLocaleString('pt-BR')} min`}</b> tempo médio</span>
              </div>

              {row.recipients === 0 && <div className="notice warning announcement-delivery-warning">Nenhum destinatário encontrado para esse público. Defina a unidade padrão dos usuários ou aguarde o check-in diário.</div>}

              {followupId === row.id && (
                <section className="announcement-followup">
                  <div className="announcement-followup-head">
                    <div><p className="eyebrow">RASTREABILIDADE</p><h3>Acompanhamento do comunicado</h3><p className="muted">Use esta visão para localizar quem ainda precisa receber ou confirmar a orientação.</p></div>
                    <div className="announcement-followup-head-actions">
                      {row.status === 'encerrado' && <button className="secondary-button compact" type="button" disabled={followupLoading || followupRows.length === 0} onClick={() => void exportReport(row)}>Gerar relatório PDF</button>}
                      <div className="announcement-followup-summary">
                        <span className="unseen"><b>{followupCounts.nao_visualizado}</b> não visualizaram</span>
                        <span className="seen"><b>{followupCounts.visualizado}</b> sem ciência</span>
                        <span className="ack"><b>{followupCounts.ciente}</b> cientes</span>
                      </div>
                    </div>
                  </div>

                  {followupLoading && <div className="empty-state">Carregando acompanhamento...</div>}
                  {followupError && <div className="notice error">{followupError}</div>}

                  {!followupLoading && !followupError && (
                    <>
                      <div className="announcement-followup-filters">
                        <input type="search" placeholder="Buscar colaborador, e-mail ou unidade..." value={followupSearch} onChange={(event) => setFollowupSearch(event.target.value)} />
                        <select value={followupStatus} onChange={(event) => setFollowupStatus(event.target.value as 'todos' | AnnouncementFollowupStatus)}>
                          <option value="todos">Todos os status</option>
                          <option value="nao_visualizado">Não visualizaram</option>
                          <option value="visualizado">Visualizaram sem ciência</option>
                          <option value="ciente">Cientes</option>
                        </select>
                        <select value={followupUnit} onChange={(event) => setFollowupUnit(event.target.value)}>
                          <option value="todos">Todas as unidades</option>
                          {followupUnits.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                        </select>
                      </div>

                      <div className="announcement-followup-table-wrap">
                        <table className="announcement-followup-table">
                          <thead><tr><th>Colaborador</th><th>Unidade</th><th>Situação</th><th>Visualizou</th><th>Ciência</th></tr></thead>
                          <tbody>
                            {filteredFollowup.map((item) => (
                              <tr key={item.user_id}>
                                <td><strong>{item.nome}</strong><small>{item.email}</small></td>
                                <td>{item.unit_label}</td>
                                <td><span className={`announcement-followup-status ${item.status}`}>{FOLLOWUP_LABELS[item.status]}</span></td>
                                <td>{formatDate(item.first_seen_at)}</td>
                                <td>{formatDate(item.acknowledged_at)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {filteredFollowup.length === 0 && <div className="empty-state">Nenhum colaborador corresponde aos filtros selecionados.</div>}
                      </div>
                    </>
                  )}
                </section>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="notice info">Após o comunicado ser encerrado e o relatório PDF ser gerado, inicia-se uma retenção de 24 horas. Depois desse prazo, mensagem, destinatários e rastreabilidade nominal são excluídos; permanecem somente indicadores estatísticos anônimos. A pesquisa de clima continua separada desta funcionalidade.</div>
    </section>
  )
}
